import ListItemText from "@material-ui/core/ListItemText";
import React, { useContext } from "react";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import { StyledMenuItem } from "../../../components/Menu/StyledMenu";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import lowercaseInitials from "../../../functions/lowerCaseInitials.function";
import Dictionary from "../../../models/Dictionary";
import { tesaBlue } from "../../../styles/colors";

type TestRequestProcessInterpolations = {
  actionDisplayName: string;
  testRequestTitle: string;
  type: "APROE";
};
type TestRequestInterpolations = {
  action: string;
  testRequestTitle: string;
  type: "REGULAR";
};

type InterpolationConfig = TestRequestInterpolations | TestRequestProcessInterpolations;

function getDefaultText(interpolationConfig: InterpolationConfig) {
  if (interpolationConfig.type === "APROE") {
    const { actionDisplayName, testRequestTitle } = interpolationConfig;
    return (
      <>
        {`You are about set `}
        <b>{testRequestTitle}</b>
        {` ${actionDisplayName.split(" ").slice(1).join(" ")} `}
      </>
    );
  }
  const { action, testRequestTitle } = interpolationConfig;
  return (
    <>
      {`You are about to ${action} `}
      <b>{testRequestTitle}</b>
    </>
  );
}

export interface TestRequestActionsMenuItemProps<T extends string, U> {
  altDialogHeader?: string;
  altDialogText?: JSX.Element;
  altLabel?: string;
  altSuccessMessage?: string;
  aproe?: boolean;
  close: () => void;
  dialog?: boolean;
  disabled?: boolean;
  testRequestTitle?: string;
  type?: T;
  dictionary?: Dictionary<T>;
  onConfirm: () => Promise<U>;
  onSuccessCallback?: (testRequest: U, redirect?: boolean) => void;
  preSave?: () => Promise<U | void>;
}

function TestRequestActionsMenuItem<T extends string, U>(props: TestRequestActionsMenuItemProps<T, U>) {
  const {
    altDialogText,
    altDialogHeader,
    altLabel,
    altSuccessMessage,
    aproe,
    close,
    disabled,
    testRequestTitle,
    type,
    dialog = true,
    dictionary,
    onConfirm,
    onSuccessCallback,
    preSave,
  } = props;
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const actionDisplayName = type && dictionary ? dictionary[type] : type || "";
  const label = altLabel || (aproe ? actionDisplayName : `${actionDisplayName} Request`);
  const action = lowercaseInitials(actionDisplayName);
  const dialogText = dialog
    ? altDialogText ||
      getDefaultText(
        aproe
          ? {
              type: "APROE",
              testRequestTitle: testRequestTitle || "",
              actionDisplayName,
            }
          : { type: "REGULAR", testRequestTitle: testRequestTitle || "", action },
      )
    : "";
  const successMessage = dialog
    ? altSuccessMessage ||
      (aproe
        ? `The test request was set ${actionDisplayName.split(" ").slice(1).join(" ")}.`
        : `The test request was ${action}ed.`)
    : "";

  const doAction = () =>
    onConfirm()
      .then((data) => {
        if (successMessage) {
          showSnackbar(successMessage, "success");
        }
        if (onSuccessCallback) {
          onSuccessCallback(data);
        }
      })
      .catch(handleHttpError);

  const maybeDialog = (showDialog: boolean) =>
    showDialog
      ? showConfirmationDialog({
          headline: altDialogHeader,
          text: dialogText,
          confirmText: actionDisplayName,
          confirmColor: tesaBlue,
        })
      : Promise.resolve("");

  const handleClick = () => {
    close();
    maybeDialog(!!dialog)
      .then(() => (preSave ? preSave().then(doAction) : doAction()))
      // must catch hidden error on dialog close
      .catch(handleHttpError);
  };
  return (
    <StyledMenuItem disabled={disabled} onClick={handleClick}>
      <ListItemText primary={label} />
    </StyledMenuItem>
  );
}

export default TestRequestActionsMenuItem;
